import React, { useMemo, useRef } from "react";
import type { ReactElement, Dispatch } from "react";

import { Box, HStack, Button } from "native-base";
import { ScrollView } from "react-native";
import type { PreloadedQuery } from "react-relay";

import BlacklistedDates from "pianofunclub-crm/components/Schools/BlacklistedDates";
import type { BlacklistedDatesReducerValues } from "pianofunclub-crm/components/Schools/BlacklistedDates";
import YearTermDates from "pianofunclub-crm/components/Schools/YearTermDates";
import type { YearTermDatesReducerValues } from "pianofunclub-crm/components/Schools/YearTermDates";

import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import { RestartIcon } from "pianofunclub-shared/components/Other/Icons";
import type { LoadBlacklistedDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadBlacklistedDatesQuery.graphql";
import type { LoadTermDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadTermDatesQuery.graphql";
import type { State, Action } from "pianofunclub-shared/utils/reducers";

export interface SchoolDatesReducerValues
  extends YearTermDatesReducerValues,
    BlacklistedDatesReducerValues {}

interface Props {
  dispatchScreenState: Dispatch<Action<SchoolDatesReducerValues>>;
  loadBlacklistedDatesQueryReference:
    | PreloadedQuery<LoadBlacklistedDatesQuery, Record<string, unknown>>
    | undefined
    | null;
  loadTermDatesQueryReference:
    | PreloadedQuery<LoadTermDatesQuery, Record<string, unknown>>
    | undefined
    | null;
  navigation: any;
  refreshHandler: () => void;
  schoolId?: string;
  screenState: State<SchoolDatesReducerValues>;
  sideBarWidth: number;
  startingYears: {
    label: string;
    value: number;
  }[];
}

const SchoolDates = (props: Props): ReactElement => {
  const {
    dispatchScreenState,
    loadBlacklistedDatesQueryReference,
    loadTermDatesQueryReference,
    navigation,
    refreshHandler,
    schoolId,
    screenState,
    sideBarWidth,
    startingYears,
  } = props;

  const scrollViewRef = useRef<ScrollView>(null);

  const termDatesInputKey = useRef(0);

  const renderHeader = useMemo(() => {
    return (
      <HStack mb="8" space="4">
        <Select
          borderRadius="2xl"
          fontSize="md"
          onValueChange={(itemValue) => {
            dispatchScreenState({
              input: "startingYear",
              value: parseInt(itemValue),
            });
            navigation.setParams({
              startingYear: parseInt(itemValue),
            });
          }}
          placeholder="Select year"
          selectedValue={String(screenState.values.startingYear)}
          width="40"
        >
          {startingYears.map((item) => {
            return (
              <Select.Item
                key={item.value}
                actionSheetLabel={item.label}
                value={String(item.value)}
              />
            );
          })}
        </Select>
        <Button
          bg="surface.400"
          colorScheme="surface"
          leftIcon={<RestartIcon size="5" />}
          onPress={() => {
            if (screenState.values.termDatesHaveBeenModified) {
              dispatchScreenState({
                input: "leaveAlertAction",
                value: () => refreshHandler(),
              });
            } else {
              refreshHandler();
            }
          }}
          p="3"
        />
      </HStack>
    );
  }, [
    screenState.values.startingYear,
    screenState.values.termDatesHaveBeenModified,
    startingYears,
    dispatchScreenState,
    navigation,
    refreshHandler,
  ]);

  return (
    <ScrollView
      ref={scrollViewRef}
      contentContainerStyle={{
        paddingVertical: 24,
      }}
      showsVerticalScrollIndicator={false}
      style={{
        flex: 1,
        marginTop: -24,
      }}
    >
      {renderHeader}
      <YearTermDates
        key={termDatesInputKey.current}
        dispatchScreenState={dispatchScreenState}
        loadTermDatesQueryReference={loadTermDatesQueryReference}
        navigation={navigation}
        schoolId={schoolId}
        screenState={screenState}
      />
      <Box mt="90px">
        <BlacklistedDates
          loadBlacklistedDatesQueryReference={loadBlacklistedDatesQueryReference}
          navigation={navigation}
          schoolId={schoolId}
          screenState={screenState}
          scrollViewRef={scrollViewRef}
          sideBarWidth={sideBarWidth}
        />
      </Box>
    </ScrollView>
  );
};

export default SchoolDates;
